import * as React from 'react';
import { Image, Pressable, SafeAreaView, ScrollView, StyleSheet, TextInput, useWindowDimensions, View } from 'react-native';
import * as AppUtilities from "../context/AppUtilities";
import * as AppConstants from "../context/AppConstants";
import * as AppState from "../context/AppState";
import { useSelector, useDispatch } from 'react-redux';
import { SegmentedButtons } from 'react-native-paper';
import { Divider, Text } from 'react-native-paper';
import { Snackbar } from 'react-native-paper';
import { GooglePlacesAutocomplete } from 'react-native-google-places-autocomplete';
import { CustomButton } from '../components/CustomButton';
import { CustomCheckbox } from '../components/CustomCheckbox';
import { SearchTextInput } from '../components/SearchTextInput';
import { useNavigation } from '@react-navigation/native';
import { Picker } from '@react-native-picker/picker';

const PADDING_RIGHT = 40;

const styles = StyleSheet.create({
  mainContainer: { flex: 1 },
  mainScrollArea: {
    backgroundColor: "#ffffff",
    flexGrow: 1,
    padding: 20,
  },
  iconGPS: { width: 24, height: 24, },
  icon2: { width: 18, height: 18, marginLeft: 5 },
  rowWrapped: { flexDirection: "row", flexWrap: "wrap" },
  searchInput: {
    paddingVertical: 10,
    paddingLeft: 10,
    borderWidth: 1,
    borderColor: "#cccccc",
    borderRadius: 5,
  },
  locationContainer: { flexDirection: "row", alignItems: "center", marginBottom: 20, paddingHorizontal: 10, borderWidth: 1, borderColor: "#cccccc", borderRadius: 5 },
  container1: { flexDirection: "row", columnGap: 5, alignItems: "center", paddingBottom: 10 },
  container2: { flexDirection: "row", height: 28, paddingBottom: 10, columnGap: 5, alignItems: "center" },
  container3: { flexDirection: "row", columnGap: 10, marginBottom: 10, alignItems: "center" },
  container4: { marginVertical: 20 },
  container5: { flexDirection: "row", flexWrap: "wrap", columnGap: 20 },
  container6: { padding: 10, },
  container7: { flexDirection: "row", flexWrap: "wrap", paddingTop: 20 },
  divider1: { width: 175 },
  divider2: { width: "100%" },
  optionContainer: {
    margin: 20,
    marginLeft: 0,
    width: 400,
  },
  line2: {
    marginVertical: 10,
    flexWrap: "wrap",
    flexDirection: "row",
    alignItems: "center",
    columnGap: 10,
    marginLeft: 10,
    marginRight: 10,
  },
  buttonsContainer: {
    //paddingLeft: 45
    paddingVertical: 5,
  },
  buttonsContainer2: {
    //paddingLeft: 45
    //paddingVertical: 20,
    //paddingTop: 30,
    flexDirection: "row",
    alignSelf: "center",
    gap: 5,
  },
  containerWithPadding1: {
    flex: 1,
    paddingRight: PADDING_RIGHT,
    paddingTop: 30,
  },
  containerWithPadding2: {
    paddingRight: PADDING_RIGHT,
    paddingTop: 30,
  },
  containerWithPadding3: {
    paddingRight: 20,
    paddingTop: 30,
  },
  containerWithPadding4: {
    paddingRight: PADDING_RIGHT,
    paddingTop: 10,
  },
});

const AUTOCOMPLETE_STYLES = {
  // https://github.com/FaridSafi/react-native-google-places-autocomplete?tab=readme-ov-file#default-styles
  container: {
    flex: 1,
  },
  textInputContainer: {
    flexDirection: 'row',
    alignItems: "center",
  },
  textInput: {
    backgroundColor: '#FFFFFF',
    borderWidth: 0,
    flex: 1,
    paddingVertical: 10,
    paddingLeft: 5,  
  },
  row: {
    backgroundColor: '#FFFFFF',
    padding: 13,
    height: 44,
    flexDirection: 'row',
  },
  separator: {
    height: 0.5,
    backgroundColor: '#c8c7cc',
  },
  loader: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    height: 20,
  },
};

const CHECKBOX_GROUPS = Object.freeze({
  PRODUCT_CATEGORIES: "PRODUCT_CATEGORIES",
  WELFARE_CERTIFICATIONS: "WELFARE_CERTIFICATIONS",
  EXTRA_ANIMAL_WELFARE: "EXTRA_ANIMAL_WELFARE",
  INGREDIENTS: "INGREDIENTS",
  LOCATION_FLAGS: "LOCATION_FLAGS",
});

const OTHER_KEYS = Object.freeze({
  ALL_PRODUCT_CATEGORIES: "ALL_PRODUCT_CATEGORIES",
  ALL_ANIMAL_WELFARE: "ALL_ANIMAL_WELFARE",
  PRODUCT_TYPES: "PRODUCT_TYPES",
  CERTIFICATION_GROUP: "CERTIFICATION_GROUP",
});

function mapHashKeysWithValue(hash, value) {
  const result = {};
  for (const productType of Object.values(hash)) {
    result[productType[AppConstants.TYPE_KEYS.URL_KEY]] = value;
  }
  return result;
}

const ALL_PRODUCT_TYPES = [
  AppConstants.SYNTHESIZED_TYPES.FOR_HUMANS[AppConstants.TYPE_KEYS.URL_KEY],
  AppConstants.SYNTHESIZED_TYPES.FOR_PETS[AppConstants.TYPE_KEYS.URL_KEY],
];

const INITIAL_SEARCH_STATE = {
  [OTHER_KEYS.ALL_PRODUCT_CATEGORIES]: true,
  [OTHER_KEYS.ALL_ANIMAL_WELFARE]: false,
  [OTHER_KEYS.PRODUCT_TYPES]: ALL_PRODUCT_TYPES,
  [OTHER_KEYS.CERTIFICATION_GROUP]: "",
  ...mapHashKeysWithValue(AppConstants.PRODUCT_TYPES, true),
};

const DISPATCHES = Object.freeze({
  UPDATE_PRODUCT_CATEGORY_VALUE: "UPDATE_PRODUCT_CATEGORY_VALUE",
  UPDATE_SINGLE_VALUE: "UPDATE_SINGLE_VALUE",
  UPDATE_VALUES_BY_SET: "UPDATE_VALUES_BY_SET",
  RESET_VALUES_BY_SET: "RESET_VALUES_BY_SET",
  UPDATE_PRODUCT_TYPES: "UPDATE_PRODUCT_TYPES",
  UPDATE_CERTIFICATION_GROUP: "UPDATE_CERTIFICATION_GROUP",
});

function areAllSet(state, keyHash) {
  for (const toCheck of Object.values(keyHash)) {
    if (!state[toCheck[AppConstants.TYPE_KEYS.URL_KEY]]) {
      return false;
    }
  }
  return true;
}

function areAllNotSet(state, keyHash) {
  for (const toCheck of Object.values(keyHash)) {
    if (state[toCheck[AppConstants.TYPE_KEYS.URL_KEY]]) {
      return false;
    }
  }
  return true;
}

function areAnySet(state, keyHash) {
  return !areAllNotSet(state, keyHash);
}

function doesKeyHashMatchArray(array, keyHash) {
  for (const certificationType of Object.values(AppConstants.WELFARE_CERTIFICATIONS)) {
    const urlKey = certificationType[AppConstants.TYPE_KEYS.URL_KEY];
    if (array.find((x) => x == urlKey)) {
      if (!keyHash[urlKey]) {
        return false;
      }
    } else {
      if (keyHash[urlKey]) {
        return false;
      }
    }
  }
  return true;
}

function isKey(key, keyHash) {
  for (const productType of Object.values(keyHash)) {
    if (productType[AppConstants.TYPE_KEYS.URL_KEY] == key) {
      return true;
    }
  }
  return false;
}

function addKeysToGroupArray(array, groupKey) {
  const filterValues = AppConstants.FILTER_RECOMMENDED_VALUES_UNIQUE[groupKey];
  if (filterValues) {
    for (const item of Object.keys(filterValues)) {
      for (const certificationAttribute of Object.values(AppConstants.WELFARE_CERTIFICATIONS)) {
        if (certificationAttribute[AppConstants.TYPE_KEYS.DATABASE_KEY] == item) {
          array.push(certificationAttribute[AppConstants.TYPE_KEYS.URL_KEY]);
        }
      }
    }
  }
}

function areKeysExactlyInHash(hash, keys) {
  for (const key of keys) {
    if (!(key in hash)) {
      return false;
    }
  }
  for (const key of Object.keys(hash)) {
    if (!keys.includes(key)) {
      return false;
    }
  }
  return true;
}

const GROUP_PASTURE_KEYS = [];
addKeysToGroupArray(GROUP_PASTURE_KEYS, AppConstants.GROUP_TYPES.PASTURE[AppConstants.TYPE_KEYS.URL_KEY]);

const GROUP_OUTDOORS_KEYS = [];
addKeysToGroupArray(GROUP_OUTDOORS_KEYS, AppConstants.GROUP_TYPES.PASTURE[AppConstants.TYPE_KEYS.URL_KEY]);
addKeysToGroupArray(GROUP_OUTDOORS_KEYS, AppConstants.GROUP_TYPES.OUTDOORS[AppConstants.TYPE_KEYS.URL_KEY]);

const GROUP_INDOORS_KEYS = [];
addKeysToGroupArray(GROUP_INDOORS_KEYS, AppConstants.GROUP_TYPES.PASTURE[AppConstants.TYPE_KEYS.URL_KEY]);
addKeysToGroupArray(GROUP_INDOORS_KEYS, AppConstants.GROUP_TYPES.OUTDOORS[AppConstants.TYPE_KEYS.URL_KEY]);
addKeysToGroupArray(GROUP_INDOORS_KEYS, AppConstants.GROUP_TYPES.INDOORS[AppConstants.TYPE_KEYS.URL_KEY]);

function setAllCertificationsInArray(newState, array) {
  for (const key of array) {
    newState[key] = true;
  }
}

function searchStateReducer(prevState, action) {
  switch (action.type) {
    case DISPATCHES.UPDATE_PRODUCT_CATEGORY_VALUE: {
      let newState = {
        ...prevState,
        [action.key]: action.value,
      };
      if (isKey(action.key, AppConstants.PRODUCT_TYPES) && !action.value) {
        newState[OTHER_KEYS.ALL_PRODUCT_CATEGORIES] = false;
      }
      if (action.key == OTHER_KEYS.ALL_PRODUCT_CATEGORIES) {
        newState = Object.assign(newState, mapHashKeysWithValue(AppConstants.PRODUCT_TYPES, action.value));
        if (action.value) {
          newState[OTHER_KEYS.PRODUCT_TYPES] = ALL_PRODUCT_TYPES;
        }
      } else {
        if (!newState[AppConstants.PRODUCT_TYPES.FOR_DOGS[AppConstants.TYPE_KEYS.URL_KEY]] &&
            !newState[AppConstants.PRODUCT_TYPES.FOR_CATS[AppConstants.TYPE_KEYS.URL_KEY]]) {
          // If all pet food categories are disabled, then ensure the for pets selection is disabled
          if (newState[OTHER_KEYS.PRODUCT_TYPES].length == 1 && newState[OTHER_KEYS.PRODUCT_TYPES][0] == AppConstants.PRODUCT_TYPES.FOR_DOGS[AppConstants.TYPE_KEYS.URL_KEY]) {
            newState[OTHER_KEYS.PRODUCT_TYPES] = [];
          } else if (newState[OTHER_KEYS.PRODUCT_TYPES].length == 2) {
            newState[OTHER_KEYS.PRODUCT_TYPES] = [AppConstants.SYNTHESIZED_TYPES.FOR_HUMANS[AppConstants.TYPE_KEYS.URL_KEY]];
          }
        } else if (newState[AppConstants.PRODUCT_TYPES.FOR_DOGS[AppConstants.TYPE_KEYS.URL_KEY]] &&
                   newState[AppConstants.PRODUCT_TYPES.FOR_CATS[AppConstants.TYPE_KEYS.URL_KEY]]) {
          // If all pet food categories are enabled, then ensure the for pets selection is enabled
          if (newState[OTHER_KEYS.PRODUCT_TYPES].length == 0) {
            newState[OTHER_KEYS.PRODUCT_TYPES] = [AppConstants.SYNTHESIZED_TYPES.FOR_PETS[AppConstants.TYPE_KEYS.URL_KEY]];
          } else if (newState[OTHER_KEYS.PRODUCT_TYPES].length == 1 && newState[OTHER_KEYS.PRODUCT_TYPES][0] == AppConstants.SYNTHESIZED_TYPES.FOR_HUMANS[AppConstants.TYPE_KEYS.URL_KEY]) {
            newState[OTHER_KEYS.PRODUCT_TYPES] = ALL_PRODUCT_TYPES;
          }
        }

        if (newState[OTHER_KEYS.PRODUCT_TYPES].length == 0) {
          if (areAnySet(newState, AppConstants.PRODUCT_TYPES)) {
            newState[OTHER_KEYS.PRODUCT_TYPES] = [AppConstants.SYNTHESIZED_TYPES.FOR_HUMANS[AppConstants.TYPE_KEYS.URL_KEY]];
          }
        }

        /*

        // Old logic with subtle mistakes!

        let areAnyNonPetFoodSet = false;
        for (const productType of Object.values(AppConstants.PRODUCT_TYPES)) {
          if (newState[productType[AppConstants.TYPE_KEYS.URL_KEY]] &&
              productType[AppConstants.TYPE_KEYS.URL_KEY] != AppConstants.PRODUCT_TYPES.FOR_DOGS[AppConstants.TYPE_KEYS.URL_KEY] &&
              productType[AppConstants.TYPE_KEYS.URL_KEY] != AppConstants.PRODUCT_TYPES.FOR_CATS[AppConstants.TYPE_KEYS.URL_KEY]) {
            areAnyNonPetFoodSet = true;
          }
        }

        if (!newState[AppConstants.PRODUCT_TYPES.FOR_DOGS[AppConstants.TYPE_KEYS.URL_KEY]] && !newState[AppConstants.PRODUCT_TYPES.FOR_CATS[AppConstants.TYPE_KEYS.URL_KEY]]) {
          if (areAnyNonPetFoodSet) {
            newState[OTHER_KEYS.PRODUCT_TYPES] = [AppConstants.SYNTHESIZED_TYPES.FOR_HUMANS[AppConstants.TYPE_KEYS.URL_KEY]];
          } else {
            newState[OTHER_KEYS.PRODUCT_TYPES] = [];
          }
        } else if (newState[AppConstants.PRODUCT_TYPES.FOR_DOGS[AppConstants.TYPE_KEYS.URL_KEY]] && newState[AppConstants.PRODUCT_TYPES.FOR_CATS[AppConstants.TYPE_KEYS.URL_KEY]]) {
          if (areAllSet(newState, AppConstants.PRODUCT_TYPES)) {
            newState[OTHER_KEYS.PRODUCT_TYPES] = ALL_PRODUCT_TYPES;
            newState[OTHER_KEYS.ALL_PRODUCT_CATEGORIES] = true;
          } else if (areAnyNonPetFoodSet) {
            newState[OTHER_KEYS.PRODUCT_TYPES] = ALL_PRODUCT_TYPES;
          } else {
            newState[OTHER_KEYS.PRODUCT_TYPES] = [AppConstants.SYNTHESIZED_TYPES.FOR_PETS[AppConstants.TYPE_KEYS.URL_KEY]];
          }
        }
        */
      }
      return newState;
    }
    case DISPATCHES.UPDATE_VALUES_BY_SET: {
      return {
        ...prevState,
        ...action.set,
      };
    }
    case DISPATCHES.RESET_VALUES_BY_SET: {
      AppUtilities.info("Search RESET_VALUES_BY_SET");
      AppUtilities.dir(action.set);
      return {
        ...action.set,
      };
    }
    case DISPATCHES.UPDATE_PRODUCT_TYPES: {
      const newState = {
        ...prevState,
        [OTHER_KEYS.PRODUCT_TYPES]: action.newValues,
      };

      const wasSelectedHumans = prevState[OTHER_KEYS.PRODUCT_TYPES].find((element) => element == AppConstants.SYNTHESIZED_TYPES.FOR_HUMANS[AppConstants.TYPE_KEYS.URL_KEY]);
      const wasSelectedPets = prevState[OTHER_KEYS.PRODUCT_TYPES].find((element) => element == AppConstants.SYNTHESIZED_TYPES.FOR_PETS[AppConstants.TYPE_KEYS.URL_KEY]);

      const isSelectedHumans = action.newValues.find((element) => element == AppConstants.SYNTHESIZED_TYPES.FOR_HUMANS[AppConstants.TYPE_KEYS.URL_KEY]);
      const isSelectedPets = action.newValues.find((element) => element == AppConstants.SYNTHESIZED_TYPES.FOR_PETS[AppConstants.TYPE_KEYS.URL_KEY]);

      if (wasSelectedHumans && !isSelectedHumans) {
        for (const productType of Object.values(AppConstants.PRODUCT_TYPES)) {
          if (productType[AppConstants.TYPE_KEYS.URL_KEY] != AppConstants.PRODUCT_TYPES.FOR_DOGS[AppConstants.TYPE_KEYS.URL_KEY] &&
              productType[AppConstants.TYPE_KEYS.URL_KEY] != AppConstants.PRODUCT_TYPES.FOR_CATS[AppConstants.TYPE_KEYS.URL_KEY]) {
            newState[productType[AppConstants.TYPE_KEYS.URL_KEY]] = false;
          }
        }
      } else if (!wasSelectedHumans && isSelectedHumans) {
        for (const productType of Object.values(AppConstants.PRODUCT_TYPES)) {
          if (productType[AppConstants.TYPE_KEYS.URL_KEY] != AppConstants.PRODUCT_TYPES.FOR_DOGS[AppConstants.TYPE_KEYS.URL_KEY] &&
              productType[AppConstants.TYPE_KEYS.URL_KEY] != AppConstants.PRODUCT_TYPES.FOR_CATS[AppConstants.TYPE_KEYS.URL_KEY]) {
            newState[productType[AppConstants.TYPE_KEYS.URL_KEY]] = true;
          }
        }
      }

      if (wasSelectedPets && !isSelectedPets) {
        for (const productType of Object.values(AppConstants.PRODUCT_TYPES)) {
          if (productType[AppConstants.TYPE_KEYS.URL_KEY] == AppConstants.PRODUCT_TYPES.FOR_DOGS[AppConstants.TYPE_KEYS.URL_KEY] ||
              productType[AppConstants.TYPE_KEYS.URL_KEY] == AppConstants.PRODUCT_TYPES.FOR_CATS[AppConstants.TYPE_KEYS.URL_KEY]) {
            newState[productType[AppConstants.TYPE_KEYS.URL_KEY]] = false;
          }
        }
      } else if (!wasSelectedPets && isSelectedPets) {
        for (const productType of Object.values(AppConstants.PRODUCT_TYPES)) {
          if (productType[AppConstants.TYPE_KEYS.URL_KEY] == AppConstants.PRODUCT_TYPES.FOR_DOGS[AppConstants.TYPE_KEYS.URL_KEY] ||
              productType[AppConstants.TYPE_KEYS.URL_KEY] == AppConstants.PRODUCT_TYPES.FOR_CATS[AppConstants.TYPE_KEYS.URL_KEY]) {
            newState[productType[AppConstants.TYPE_KEYS.URL_KEY]] = true;
          }
        }
      }

      if (isSelectedHumans && isSelectedPets && areAllSet(newState, AppConstants.PRODUCT_TYPES)) {
        newState[OTHER_KEYS.ALL_PRODUCT_CATEGORIES] = true;
      } else if (!isSelectedHumans || !isSelectedPets) {
        newState[OTHER_KEYS.ALL_PRODUCT_CATEGORIES] = false;
      }
      
      return newState;
    }

    // Updating the outdoors/pasture/indoors radio group
    case DISPATCHES.UPDATE_CERTIFICATION_GROUP: {
      let newState = {
        ...prevState,
        [OTHER_KEYS.CERTIFICATION_GROUP]: action.newValue,
      };

      for (const certificationAttribute of Object.values(AppConstants.WELFARE_CERTIFICATIONS)) {
        newState[certificationAttribute[AppConstants.TYPE_KEYS.URL_KEY]] = false;
      }

      if (action.newValue == AppConstants.GROUP_TYPES.PASTURE[AppConstants.TYPE_KEYS.URL_KEY]) {
        setAllCertificationsInArray(newState, GROUP_PASTURE_KEYS);
      } else if (action.newValue == AppConstants.GROUP_TYPES.OUTDOORS[AppConstants.TYPE_KEYS.URL_KEY]) {
        setAllCertificationsInArray(newState, GROUP_OUTDOORS_KEYS);
      } else if (action.newValue == AppConstants.GROUP_TYPES.INDOORS[AppConstants.TYPE_KEYS.URL_KEY]) {
        setAllCertificationsInArray(newState, GROUP_INDOORS_KEYS);
      }
      
      return newState;
    }
    case DISPATCHES.UPDATE_SINGLE_VALUE: {
      let newState = {
        ...prevState,
        [action.key]: action.value,
      };

      if (action.key == OTHER_KEYS.ALL_ANIMAL_WELFARE) {
        newState[OTHER_KEYS.ALL_ANIMAL_WELFARE] = action.value;
        newState = Object.assign(newState, mapHashKeysWithValue(AppConstants.WELFARE_CERTIFICATIONS, action.value));
        newState[OTHER_KEYS.CERTIFICATION_GROUP] = "";
      } else {
        // There's no way to differentiate between the default value and an explicit off,
        // so just use the default because there's no way to tell the difference between
        // off and the other defaults
        if (!newState[action.key]) {
          delete newState[action.key];
        }

        if (action.category == CHECKBOX_GROUPS.WELFARE_CERTIFICATIONS) {
          newState[OTHER_KEYS.ALL_ANIMAL_WELFARE] = areAllSet(newState, AppConstants.WELFARE_CERTIFICATIONS);

          let certificationsSet = {};
          for (const certificationAttribute of Object.values(AppConstants.WELFARE_CERTIFICATIONS)) {
            if (newState[certificationAttribute[AppConstants.TYPE_KEYS.URL_KEY]]) {
              certificationsSet[certificationAttribute[AppConstants.TYPE_KEYS.URL_KEY]] = true;
            }
          }

          newState[OTHER_KEYS.CERTIFICATION_GROUP] = "";
          if (areKeysExactlyInHash(certificationsSet, GROUP_INDOORS_KEYS)) {
            newState[OTHER_KEYS.CERTIFICATION_GROUP] = AppConstants.GROUP_TYPES.INDOORS[AppConstants.TYPE_KEYS.URL_KEY];
          } else if (areKeysExactlyInHash(certificationsSet, GROUP_OUTDOORS_KEYS)) {
            newState[OTHER_KEYS.CERTIFICATION_GROUP] = AppConstants.GROUP_TYPES.OUTDOORS[AppConstants.TYPE_KEYS.URL_KEY];
          } else if (areKeysExactlyInHash(certificationsSet, GROUP_PASTURE_KEYS)) {
            newState[OTHER_KEYS.CERTIFICATION_GROUP] = AppConstants.GROUP_TYPES.PASTURE[AppConstants.TYPE_KEYS.URL_KEY];
          }
        }
      }

      return newState;
    }
  }
}

export function Search({ setSearchVisible, hideLocationSearch }) {
  //const { width } = useWindowDimensions();
  const dataFilter = useSelector((state) => state.dataFilter);
  const globalDispatch = useDispatch();
  const navigation = useNavigation();

  const searchInputRef = React.useRef();
  const autocompleteRef = React.useRef();
  const [searchLocationText, setSearchLocationText] = React.useState(null);
  const [searchLocationLatitude, setSearchLocationLatitude] = React.useState(null);
  const [searchLocationLongitude, setSearchLocationLongitude] = React.useState(null);
  const [searchLocationZoom, setSearchLocationZoom] = React.useState(null);
  const [producer, setProducer] = React.useState("");
  const [savePermanently, setSavePermanently] = React.useState(false);
  const [allProducers, setAllProducers] = React.useState([]);

  const [snackVisible, setSnackVisible] = React.useState(false);
  const [snackText, setSnackText] = React.useState("");

  const [searchState, dispatchSearchState] = React.useReducer(
    searchStateReducer,
    INITIAL_SEARCH_STATE,
  );

  function showTemporarySnack(message) {
    setSnackText(message);
    setSnackVisible(true);
  }

  function navigateToFilter() {
    navigation.navigate(
      AppConstants.SCREENS.HOME,
      {
        screen: AppConstants.SCREENS.MORE_STACK,
        params: {
          screen: AppConstants.SCREENS.FILTER,
        }
      }
    );
  }

  function onReset() {
    const newSearchState = Object.assign({}, INITIAL_SEARCH_STATE, processStateToLoad(AppConstants.getDefaultDataFilter(), true));
    //AppUtilities.debug("Search.onReset newSearchState");
    //AppUtilities.dir(newSearchState);
    dispatchSearchState({ type: DISPATCHES.RESET_VALUES_BY_SET, set: newSearchState });
    //showTemporarySnack("Reset complete. Customize and then press Search.");
    //closeSearch();
  }

  function processStateToLoad(filter, forceReset) {
    const newSearchState = {
      [OTHER_KEYS.ALL_PRODUCT_CATEGORIES]: true,
    };

    if (!AppUtilities.isBlank(filter[AppConstants.SYNTHESIZED_TYPES.GENERAL_SEARCH[AppConstants.TYPE_KEYS.URL_KEY]])) {
      searchInputRef.current.setText(filter[AppConstants.SYNTHESIZED_TYPES.GENERAL_SEARCH[AppConstants.TYPE_KEYS.URL_KEY]]);
    } else if (forceReset) {
      searchInputRef.current.setText("");
    }

    if (filter[AppConstants.GROUP_TYPES.PASTURE[AppConstants.TYPE_KEYS.URL_KEY]] == "y") {
      newSearchState[OTHER_KEYS.CERTIFICATION_GROUP] = AppConstants.GROUP_TYPES.PASTURE[AppConstants.TYPE_KEYS.URL_KEY];
    } else if (filter[AppConstants.GROUP_TYPES.OUTDOORS[AppConstants.TYPE_KEYS.URL_KEY]] == "y") {
      newSearchState[OTHER_KEYS.CERTIFICATION_GROUP] = AppConstants.GROUP_TYPES.OUTDOORS[AppConstants.TYPE_KEYS.URL_KEY];
    } else if (filter[AppConstants.GROUP_TYPES.INDOORS[AppConstants.TYPE_KEYS.URL_KEY]] == "y") {
      newSearchState[OTHER_KEYS.CERTIFICATION_GROUP] = AppConstants.GROUP_TYPES.INDOORS[AppConstants.TYPE_KEYS.URL_KEY];
    }

    if (filter[AppConstants.SYNTHESIZED_TYPES.FOR_HUMANS[AppConstants.TYPE_KEYS.URL_KEY]] == "y" &&
        filter[AppConstants.SYNTHESIZED_TYPES.FOR_PETS[AppConstants.TYPE_KEYS.URL_KEY]] == "y") {
      newSearchState[OTHER_KEYS.PRODUCT_TYPES] = ALL_PRODUCT_TYPES;
    } else if (filter[AppConstants.SYNTHESIZED_TYPES.FOR_HUMANS[AppConstants.TYPE_KEYS.URL_KEY]] == "y") {
      newSearchState[OTHER_KEYS.PRODUCT_TYPES] = [AppConstants.SYNTHESIZED_TYPES.FOR_HUMANS[AppConstants.TYPE_KEYS.URL_KEY]];
    } else if (filter[AppConstants.SYNTHESIZED_TYPES.FOR_PETS[AppConstants.TYPE_KEYS.URL_KEY]] == "y") {
      newSearchState[OTHER_KEYS.PRODUCT_TYPES] = [AppConstants.SYNTHESIZED_TYPES.FOR_PETS[AppConstants.TYPE_KEYS.URL_KEY]];
    } else {
      newSearchState[OTHER_KEYS.PRODUCT_TYPES] = ALL_PRODUCT_TYPES;
    }

    for (const productType of Object.values(AppConstants.PRODUCT_TYPES)) {
      if (!AppUtilities.isBlank(filter[productType[AppConstants.TYPE_KEYS.URL_KEY]])) {
        if (filter[productType[AppConstants.TYPE_KEYS.URL_KEY]] == "y") {
          newSearchState[productType[AppConstants.TYPE_KEYS.URL_KEY]] = true;
        } else if (filter[productType[AppConstants.TYPE_KEYS.URL_KEY]] == "n") {
          newSearchState[productType[AppConstants.TYPE_KEYS.URL_KEY]] = false;
          newSearchState[OTHER_KEYS.ALL_PRODUCT_CATEGORIES] = false;
        }
      }
    }

    for (const toggleType of Object.values(AppConstants.WELFARE_CERTIFICATIONS)) {
      if (!AppUtilities.isBlank(filter[toggleType[AppConstants.TYPE_KEYS.URL_KEY]])) {
        if (filter[toggleType[AppConstants.TYPE_KEYS.URL_KEY]] == "y") {
          newSearchState[toggleType[AppConstants.TYPE_KEYS.URL_KEY]] = true;
        } else if (filter[toggleType[AppConstants.TYPE_KEYS.URL_KEY]] == "n") {
          newSearchState[toggleType[AppConstants.TYPE_KEYS.URL_KEY]] = false;
        }
      }
    }

    for (const toggleType of Object.values(AppConstants.EXTRA_ANIMAL_WELFARE)) {
      if (!AppUtilities.isBlank(filter[toggleType[AppConstants.TYPE_KEYS.URL_KEY]])) {
        if (filter[toggleType[AppConstants.TYPE_KEYS.URL_KEY]] == "y") {
          newSearchState[toggleType[AppConstants.TYPE_KEYS.URL_KEY]] = true;
        } else if (filter[toggleType[AppConstants.TYPE_KEYS.URL_KEY]] == "n") {
          newSearchState[toggleType[AppConstants.TYPE_KEYS.URL_KEY]] = false;
        }
      }
    }

    for (const toggleType of Object.values(AppConstants.INGREDIENTS)) {
      if (!AppUtilities.isBlank(filter[toggleType[AppConstants.TYPE_KEYS.URL_KEY]])) {
        if (filter[toggleType[AppConstants.TYPE_KEYS.URL_KEY]] == "y") {
          newSearchState[toggleType[AppConstants.TYPE_KEYS.URL_KEY]] = true;
        } else if (filter[toggleType[AppConstants.TYPE_KEYS.URL_KEY]] == "n") {
          newSearchState[toggleType[AppConstants.TYPE_KEYS.URL_KEY]] = false;
        }
      }
    }

    for (const toggleType of Object.values(AppConstants.LOCATION_FLAGS)) {
      if (!AppUtilities.isBlank(filter[toggleType[AppConstants.TYPE_KEYS.URL_KEY]])) {
        if (filter[toggleType[AppConstants.TYPE_KEYS.URL_KEY]] == "y") {
          newSearchState[toggleType[AppConstants.TYPE_KEYS.URL_KEY]] = true;
        } else if (filter[toggleType[AppConstants.TYPE_KEYS.URL_KEY]] == "n") {
          newSearchState[toggleType[AppConstants.TYPE_KEYS.URL_KEY]] = false;
        }
      }
    }

    if (areAllNotSet(newSearchState, AppConstants.WELFARE_CERTIFICATIONS)) {
      if (newSearchState[OTHER_KEYS.CERTIFICATION_GROUP] == AppConstants.GROUP_TYPES.PASTURE[AppConstants.TYPE_KEYS.URL_KEY]) {
        setAllCertificationsInArray(newSearchState, GROUP_PASTURE_KEYS);
      } else if (newSearchState[OTHER_KEYS.CERTIFICATION_GROUP] == AppConstants.GROUP_TYPES.OUTDOORS[AppConstants.TYPE_KEYS.URL_KEY]) {
        setAllCertificationsInArray(newSearchState, GROUP_OUTDOORS_KEYS);
      } else if (newSearchState[OTHER_KEYS.CERTIFICATION_GROUP] == AppConstants.GROUP_TYPES.INDOORS[AppConstants.TYPE_KEYS.URL_KEY]) {
        setAllCertificationsInArray(newSearchState, GROUP_INDOORS_KEYS);
      }
    }

    if (!AppUtilities.isBlank(filter[AppConstants.SYNTHESIZED_TYPES.PRODUCER[AppConstants.TYPE_KEYS.URL_KEY]])) {
      setProducer(filter[AppConstants.SYNTHESIZED_TYPES.PRODUCER[AppConstants.TYPE_KEYS.URL_KEY]]);
    } else if (forceReset) {
      setProducer("");
    }

    if (forceReset) {
      autocompleteRef.current.setAddressText("");
    }

    //AppUtilities.debug("Search.processStateToLoad newSearchState:");
    //AppUtilities.dir(newSearchState);

    return newSearchState;
  }

  function closeSearch(shouldNavigateToFilter) {
    if (setSearchVisible) {
      AppUtilities.debug("Search.closeSearch shouldNavigateToFilter1: " + shouldNavigateToFilter);
      
      // This is the search popup
      setSearchVisible(false);
      if (shouldNavigateToFilter) {
        navigateToFilter();
      }
    } else {
      AppUtilities.debug("closeSearch shouldNavigateToFilter2: " + shouldNavigateToFilter);
      // This is the standalone search screen
      if (shouldNavigateToFilter) {
        navigateToFilter();
      } else {
        navigation.navigate(AppConstants.SCREENS.HOME);
      }
    }
  }

  React.useEffect(() => {
    if (dataFilter) {
      const newSearchState = processStateToLoad(dataFilter);
      dispatchSearchState({ type: DISPATCHES.UPDATE_VALUES_BY_SET, set: newSearchState });
    }
  }, [dataFilter, searchInputRef]);

  React.useEffect(() => {
    const asyncFunction = async () => {
      const response = await AppUtilities.fetchUnauthorized(
        "Search.useEffect",
        "/find_humane/api/producers?" + AppConstants.GROUP_TYPES.INDOORS[AppConstants.TYPE_KEYS.DATABASE_KEY] + "=y",
        "GET",
      );

      if (response.body) {
        setAllProducers(response.body.producers);
      }
    }
    asyncFunction();
  }, []);

  const onSearch = React.useCallback(
    async () => {
      try {
        AppUtilities.debug("Search.onSearch searchState:");
        AppUtilities.dir(searchState);

        if (searchLocationLatitude && searchLocationLongitude && searchLocationZoom) {
          globalDispatch(AppState.createDispatch(AppConstants.DISPATCH.MOVE_MAP, {
            latitude: searchLocationLatitude,
            longitude: searchLocationLongitude,
            zoom: searchLocationZoom,
            description: searchLocationText,
          }));
        }

        let certificationKey = null;
        for (const groupType of Object.values(AppConstants.GROUP_TYPES)) {
          if (groupType[AppConstants.TYPE_KEYS.URL_KEY] == searchState[OTHER_KEYS.CERTIFICATION_GROUP]) {
            certificationKey = groupType[AppConstants.TYPE_KEYS.URL_KEY];
          }
        }

        let newFilter = {
          [certificationKey]: "y",
        };

        const searchText = searchInputRef.current.getText();
        if (!AppUtilities.isBlank(searchText)) {
          newFilter[AppConstants.SYNTHESIZED_TYPES.GENERAL_SEARCH[AppConstants.TYPE_KEYS.URL_KEY]] = searchText;
        }

        if (!AppUtilities.isBlank(producer)) {
          newFilter[AppConstants.SYNTHESIZED_TYPES.PRODUCER[AppConstants.TYPE_KEYS.URL_KEY]] = producer;
        }

        if (searchState[OTHER_KEYS.PRODUCT_TYPES].length == 2) {
          // The default is to show both
        } else if (searchState[OTHER_KEYS.PRODUCT_TYPES].length == 0) {
          // It doesn't make sense to show neither
        } else if (searchState[OTHER_KEYS.PRODUCT_TYPES][0] == AppConstants.SYNTHESIZED_TYPES.FOR_HUMANS[AppConstants.TYPE_KEYS.URL_KEY]) {
          newFilter[AppConstants.SYNTHESIZED_TYPES.FOR_HUMANS[AppConstants.TYPE_KEYS.URL_KEY]] = "y";
        } else if (searchState[OTHER_KEYS.PRODUCT_TYPES][0] == AppConstants.SYNTHESIZED_TYPES.FOR_PETS[AppConstants.TYPE_KEYS.URL_KEY]) {
          newFilter[AppConstants.SYNTHESIZED_TYPES.FOR_PETS[AppConstants.TYPE_KEYS.URL_KEY]] = "y";
        }

        if (!areAllSet(searchState, AppConstants.PRODUCT_TYPES)) {
          for (const productType of Object.values(AppConstants.PRODUCT_TYPES)) {
            newFilter[productType[AppConstants.TYPE_KEYS.URL_KEY]] = searchState[productType[AppConstants.TYPE_KEYS.URL_KEY]] ? "y" : "n";
          }
        }

        for (const toggleType of Object.values(AppConstants.EXTRA_ANIMAL_WELFARE)) {
          if (searchState[toggleType[AppConstants.TYPE_KEYS.URL_KEY]]) {
            newFilter[toggleType[AppConstants.TYPE_KEYS.URL_KEY]] = "y";
          } else {
            // Defaults to no
          }
        }

        let certificationFlagsMatchGroup = false;
        if (areAnySet(searchState, AppConstants.WELFARE_CERTIFICATIONS)) {
          if (certificationKey == AppConstants.GROUP_TYPES.PASTURE[AppConstants.TYPE_KEYS.URL_KEY]) {
            certificationFlagsMatchGroup = doesKeyHashMatchArray(GROUP_PASTURE_KEYS, searchState);
          } else if (certificationKey == AppConstants.GROUP_TYPES.OUTDOORS[AppConstants.TYPE_KEYS.URL_KEY]) {
            certificationFlagsMatchGroup = doesKeyHashMatchArray(GROUP_OUTDOORS_KEYS, searchState);
          } else if (certificationKey == AppConstants.GROUP_TYPES.INDOORS[AppConstants.TYPE_KEYS.URL_KEY]) {
            certificationFlagsMatchGroup = doesKeyHashMatchArray(GROUP_INDOORS_KEYS, searchState);
          }

          if (!certificationFlagsMatchGroup) {
            for (const toggleType of Object.values(AppConstants.WELFARE_CERTIFICATIONS)) {
              if (searchState[toggleType[AppConstants.TYPE_KEYS.URL_KEY]]) {
                newFilter[toggleType[AppConstants.TYPE_KEYS.URL_KEY]] = "y";
              } else {
                // Defaults to no
              }
            }
          }
        }

        for (const toggleType of Object.values(AppConstants.INGREDIENTS)) {
          if (searchState[toggleType[AppConstants.TYPE_KEYS.URL_KEY]]) {
            newFilter[toggleType[AppConstants.TYPE_KEYS.URL_KEY]] = "y";
          } else {
            // Defaults to no
          }
        }

        for (const toggleType of Object.values(AppConstants.LOCATION_FLAGS)) {
          if (searchState[toggleType[AppConstants.TYPE_KEYS.URL_KEY]]) {
            newFilter[toggleType[AppConstants.TYPE_KEYS.URL_KEY]] = "y";
          } else {
            // Defaults to no
          }
        }

        AppUtilities.debug("Performing search with:");
        AppUtilities.dir(newFilter);

        globalDispatch(AppState.createDispatch(AppConstants.DISPATCH.UPDATE_DATA_FILTER, newFilter));

        if (savePermanently) {
          AppUtilities.info("Saving search permanently");
          await AppUtilities.updateSavedAppState("dataFilter", newFilter);
        }

        closeSearch();

      } catch (e) {
        AppUtilities.handleException(e, "Search.onSearch");
      }
    },
    [
      searchLocationText,
      searchLocationLatitude,
      searchLocationLongitude,
      searchLocationZoom,
      searchState,
      searchInputRef,
      producer,
      savePermanently,
    ],
  );

  function renderButtons(extraButtons) {
    if (!extraButtons && false) {
      return (
        <View style={styles.buttonsContainer}>
          <CustomButton onPress={onSearch}>Search</CustomButton>
        </View>
      );
    } else {
      return (
        <View style={styles.buttonsContainer2}>
          <CustomButton onPress={onSearch}>Search</CustomButton>
          <CustomButton onPress={() => closeSearch()}>Cancel</CustomButton>
          <CustomButton onPress={onReset}>Reset</CustomButton>
        </View>
      );
    }
  }

  return (
    <View style={styles.mainContainer}>
      <ScrollView contentContainerStyle={styles.mainScrollArea} keyboardShouldPersistTaps='handled'>
        <SearchTextInput
          ref={searchInputRef}
          onSubmitEditing={onSearch}
          onBackPress={closeSearch}
        />
        {
          !hideLocationSearch ?
              <View style={styles.locationContainer}>
                <GooglePlacesAutocomplete
                  ref={autocompleteRef}
                  //placeholder={searchLocationText}
                  placeholder="Near a neighborhood, city, or zip code"
                  minLength={2}
                  debounce={200}
                  autoFocus={false}
                  fetchDetails={true}
                  suppressDefaultStyles={true}
                  styles={AUTOCOMPLETE_STYLES}
                  textInputProps={{
                    placeholderTextColor: "#717171",
                    onSubmitEditing: (e) => {
                      //console.log("HERE");
                    }
                  }}
                  onSubmitEditingSelectFirst={true}
                  query={{
                    key: AppConstants.GOOGLE_API_KEY_AUTOCOMPLETE,
                    language: "en",
                  }}
                  onPress={(data, details) => {
                    let newZoom = AppConstants.ZOOM_LEVELS.CITY;
                    if (data.types) {
                      AppUtilities.info("Search: " + data.description + ", data types: " + data.types);
                      let zoomLevels = [];
                      for (let i = 0; i < data.types.length; i++) {
                        const dataType = data.types[i];
                        // https://developers.google.com/maps/documentation/geocoding/requests-geocoding#Types
                        if (dataType == "street_address" ||
                            dataType == "premise" ||
                            dataType == "subpremise" ||
                            dataType == "point_of_interest" ||
                            dataType == "plus_code" ||
                            dataType == "intersection") {
                          zoomLevels.push(AppConstants.ZOOM_LEVELS.STREETS);
                        } else if (dataType == "political") {
                          zoomLevels.push(AppConstants.ZOOM_LEVELS.COUNTRY);
                        } else if (dataType == "administrative_area_level_1") {
                          zoomLevels.push(AppConstants.ZOOM_LEVELS.STATE);
                        } else if (dataType == "locality") {
                          zoomLevels.push(AppConstants.ZOOM_LEVELS.CITY);
                        }
                      }
                      if (zoomLevels.length > 0) {
                        zoomLevels.sort((x, y) => x - y);
                        newZoom = zoomLevels[zoomLevels.length - 1];
                        AppUtilities.info("Search zoom levels: " + zoomLevels);
                      }
                    }
                    setSearchLocationText(data.description);
                    setSearchLocationLatitude(details.geometry.location.lat);
                    setSearchLocationLongitude(details.geometry.location.lng);
                    setSearchLocationZoom(newZoom);
                  }}
                  GooglePlacesDetailsQuery={{
                    fields: "geometry",
                  }}
                  enablePoweredByContainer={false}
                  timeout={10000} //ms
                  onFail={(error) => {
                    showTemporarySnack("Failed lookup: " + error);
                  }}
                  onTimeout={(data) => {
                    showTemporarySnack("Timed out. Check your internet connection.");
                  }}
                  requestUrl={{
                    useOnPlatform: 'web',
                    url: AppUtilities.getRootUri() + 'proxy/https://maps.googleapis.com/maps/api',
                  }}
                  renderLeftButton={() => {
                    return (
                      <Image source={require("../../assets/map-marker-grey.png")} style={styles.iconGPS} />
                    );
                  }}
                  predefinedPlaces={[]}
                  /*renderRightButton={() => {
                    return (
                      <Image
                        source={require("../../assets/clear-grey-18.png")}
                        style={{ width: 18, height: 18, marginLeft: 5 }}
                        onPress={onClearSearchText}
                      />
                    );
                  }}*/
                />
              </View>
            :
              null
        }
        { renderButtons() }
        <Divider style={styles.container4} />
        <Text variant="bodyLarge">Options</Text>
        <View style={AppConstants.STYLES.flex1}>
          <View style={styles.optionContainer}>
            <View style={styles.container2}>
              <Text variant="labelSmall">Product types:</Text>
            </View>
            <SegmentedButtons
              value={searchState[OTHER_KEYS.PRODUCT_TYPES]}
              onValueChange={(newValues) => dispatchSearchState({ type: DISPATCHES.UPDATE_PRODUCT_TYPES, newValues: newValues })}
              //density="small"
              multiSelect={true}
              theme={{ colors: { secondaryContainer: '#3366ff' } }}
              buttons={[
                {
                  value: AppConstants.SYNTHESIZED_TYPES.FOR_HUMANS[AppConstants.TYPE_KEYS.URL_KEY],
                  label: 'For People',
                  icon: 'human-male-female-child',
                  showSelectedCheck: true,
                  checkedColor: "#ffffff",
                },
                {
                  value: AppConstants.SYNTHESIZED_TYPES.FOR_PETS[AppConstants.TYPE_KEYS.URL_KEY],
                  label: 'For Pets',
                  icon: 'dog',
                  showSelectedCheck: true,
                  checkedColor: "#ffffff",
                },
              ]}
            />
          </View>
          <View style={styles.containerWithPadding1}>
            <View style={styles.container3}>
              <Text variant="labelLarge">Product categories:</Text>
              <CustomCheckbox
                key={OTHER_KEYS.ALL_PRODUCT_CATEGORIES}
                value={searchState[OTHER_KEYS.ALL_PRODUCT_CATEGORIES]}
                onValueChange={() => dispatchSearchState({ type: DISPATCHES.UPDATE_PRODUCT_CATEGORY_VALUE, key: OTHER_KEYS.ALL_PRODUCT_CATEGORIES, value: !searchState[OTHER_KEYS.ALL_PRODUCT_CATEGORIES] })}
                color="#3366ff"
              />
            </View>
            <Divider style={styles.divider1} />
            <View style={styles.container5}>
              {
                Object.entries(AppConstants.PRODUCT_TYPES).map(([k,v]) => (
                  <View key={v[AppConstants.TYPE_KEYS.URL_KEY]} style={styles.line2}>
                    <CustomCheckbox
                      key={v[AppConstants.TYPE_KEYS.URL_KEY]}
                      value={searchState[v[AppConstants.TYPE_KEYS.URL_KEY]] ? true : false}
                      onValueChange={() => dispatchSearchState({ category: CHECKBOX_GROUPS.PRODUCT_CATEGORIES, type: DISPATCHES.UPDATE_PRODUCT_CATEGORY_VALUE, key: v[AppConstants.TYPE_KEYS.URL_KEY], value: !searchState[v[AppConstants.TYPE_KEYS.URL_KEY]] })}
                      color="#3366ff"
                    />
                    <Text onPress={() => dispatchSearchState({ category: CHECKBOX_GROUPS.PRODUCT_CATEGORIES, type: DISPATCHES.UPDATE_PRODUCT_CATEGORY_VALUE, key: v[AppConstants.TYPE_KEYS.URL_KEY], value: !searchState[v[AppConstants.TYPE_KEYS.URL_KEY]] })}>
                      {v[AppConstants.TYPE_KEYS.TITLE]}
                    </Text>
                  </View>
                ))
              }
            </View>
          </View>
          <View style={styles.containerWithPadding2}>
            <View style={styles.container3}>
              <Text variant="labelLarge">Animal Welfare Certifications:</Text>
              <CustomCheckbox
                key={OTHER_KEYS.ALL_ANIMAL_WELFARE}
                value={searchState[OTHER_KEYS.ALL_ANIMAL_WELFARE]}
                onValueChange={() => dispatchSearchState({ category: CHECKBOX_GROUPS.WELFARE_CERTIFICATIONS, type: DISPATCHES.UPDATE_SINGLE_VALUE, key: OTHER_KEYS.ALL_ANIMAL_WELFARE, value: !searchState[OTHER_KEYS.ALL_ANIMAL_WELFARE] })}
                color="#3366ff"
              />
              <Pressable
                onPress={() => {
                  closeSearch(true);
                }}
              >
                <Image source={require('../../assets/question-grey.png')} style={AppConstants.STYLES.image18x18} />
              </Pressable>
            </View>
            <Divider style={styles.divider1} />
            <View style={styles.optionContainer}>
              <SegmentedButtons
                value={searchState[OTHER_KEYS.CERTIFICATION_GROUP]}
                onValueChange={(newValue) => dispatchSearchState({ type: DISPATCHES.UPDATE_CERTIFICATION_GROUP, newValue: newValue })}
                theme={{ colors: { secondaryContainer: '#3366ff' } }}
                //density="small"
                buttons={[
                  {
                    value: AppConstants.GROUP_TYPES.PASTURE[AppConstants.TYPE_KEYS.URL_KEY],
                    label: 'Pasture',
                    icon: props => <Image source={require('../../assets/sunpasture.png')} style={{ width: props.size, height: props.size }} />,
                    checkedColor: "#ffffff",
                  },
                  {
                    value: AppConstants.GROUP_TYPES.OUTDOORS[AppConstants.TYPE_KEYS.URL_KEY],
                    label: 'Outdoors+',
                    icon: props => <Image source={require('../../assets/sun.png')} style={{ width: props.size, height: props.size }} />,
                    checkedColor: "#ffffff",
                  },
                  {
                    value: AppConstants.GROUP_TYPES.INDOORS[AppConstants.TYPE_KEYS.URL_KEY],
                    label: 'Indoors+',
                    icon: props => <Image source={require('../../assets/factory.png')} style={{ width: props.size, height: props.size }} />,
                    checkedColor: "#ffffff",
                  },
                ]}
              />
            </View>
            <View style={styles.container5}>
              {
                Object.entries(AppConstants.WELFARE_CERTIFICATIONS).map(([k,v]) => (
                  <View key={v[AppConstants.TYPE_KEYS.URL_KEY]} style={styles.line2}>
                    <CustomCheckbox
                      key={v[AppConstants.TYPE_KEYS.URL_KEY]}
                      value={searchState[v[AppConstants.TYPE_KEYS.URL_KEY]] ? true : false}
                      onValueChange={() => dispatchSearchState({ category: CHECKBOX_GROUPS.WELFARE_CERTIFICATIONS, type: DISPATCHES.UPDATE_SINGLE_VALUE, key: v[AppConstants.TYPE_KEYS.URL_KEY], value: !searchState[v[AppConstants.TYPE_KEYS.URL_KEY]] })}
                      color="#3366ff"
                    />
                    <Text onPress={() => dispatchSearchState({ category: CHECKBOX_GROUPS.WELFARE_CERTIFICATIONS, type: DISPATCHES.UPDATE_SINGLE_VALUE, key: v[AppConstants.TYPE_KEYS.URL_KEY], value: !searchState[v[AppConstants.TYPE_KEYS.URL_KEY]] })}>
                      {v[AppConstants.TYPE_KEYS.TITLE]}
                    </Text>
                  </View>
                ))
              }
            </View>
          </View>
          <View style={styles.containerWithPadding2}>
            <View style={styles.container3}>
              <Text variant="labelLarge">Specific animal welfare:</Text>
            </View>
            <Divider style={styles.divider1} />
            <View style={styles.container5}>
              {
                Object.entries(AppConstants.EXTRA_ANIMAL_WELFARE).map(([k,v]) => (
                  <View key={v[AppConstants.TYPE_KEYS.URL_KEY]} style={styles.line2}>
                    <CustomCheckbox
                      key={v[AppConstants.TYPE_KEYS.URL_KEY]}
                      value={searchState[v[AppConstants.TYPE_KEYS.URL_KEY]] ? true : false}
                      onValueChange={() => dispatchSearchState({ category: CHECKBOX_GROUPS.EXTRA_ANIMAL_WELFARE, type: DISPATCHES.UPDATE_SINGLE_VALUE, key: v[AppConstants.TYPE_KEYS.URL_KEY], value: !searchState[v[AppConstants.TYPE_KEYS.URL_KEY]] })}
                      color="#3366ff"
                    />
                    <Text onPress={() => dispatchSearchState({ category: CHECKBOX_GROUPS.EXTRA_ANIMAL_WELFARE, type: DISPATCHES.UPDATE_SINGLE_VALUE, key: v[AppConstants.TYPE_KEYS.URL_KEY], value: !searchState[v[AppConstants.TYPE_KEYS.URL_KEY]] })}>
                      {v[AppConstants.TYPE_KEYS.TITLE]}
                    </Text>
                  </View>
                ))
              }
            </View>
          </View>
          <View style={styles.containerWithPadding2}>
            <View style={styles.container3}>
              <Text variant="labelLarge">Ingredients:</Text>
            </View>
            <Divider style={styles.divider1} />
            <View style={styles.container5}>
              {
                Object.entries(AppConstants.INGREDIENTS).map(([k,v]) => (
                  <View key={v[AppConstants.TYPE_KEYS.URL_KEY]} style={styles.line2}>
                    <CustomCheckbox
                      key={v[AppConstants.TYPE_KEYS.URL_KEY]}
                      value={searchState[v[AppConstants.TYPE_KEYS.URL_KEY]] ? true : false}
                      onValueChange={() => dispatchSearchState({ type: DISPATCHES.UPDATE_SINGLE_VALUE, key: v[AppConstants.TYPE_KEYS.URL_KEY], value: !searchState[v[AppConstants.TYPE_KEYS.URL_KEY]] })}
                      color="#3366ff"
                    />
                    <Text onPress={() => dispatchSearchState({ type: DISPATCHES.UPDATE_SINGLE_VALUE, key: v[AppConstants.TYPE_KEYS.URL_KEY], value: !searchState[v[AppConstants.TYPE_KEYS.URL_KEY]] })}>
                      {v[AppConstants.TYPE_KEYS.TITLE]}
                    </Text>
                  </View>
                ))
              }
            </View>
          </View>
          <View style={styles.containerWithPadding2}>
            <View style={styles.container3}>
              <Text variant="labelLarge">Location attributes:</Text>
            </View>
            <Divider style={styles.divider1} />
            <View style={styles.container5}>
              {
                Object.entries(AppConstants.LOCATION_FLAGS).map(([k,v]) => (
                  <View key={v[AppConstants.TYPE_KEYS.URL_KEY]} style={styles.line2}>
                    <CustomCheckbox
                      key={v[AppConstants.TYPE_KEYS.URL_KEY]}
                      value={searchState[v[AppConstants.TYPE_KEYS.URL_KEY]] ? true : false}
                      onValueChange={() => dispatchSearchState({ category: CHECKBOX_GROUPS.LOCATION_FLAGS, type: DISPATCHES.UPDATE_SINGLE_VALUE, key: v[AppConstants.TYPE_KEYS.URL_KEY], value: !searchState[v[AppConstants.TYPE_KEYS.URL_KEY]] })}
                      color="#3366ff"
                    />
                    <Text onPress={() => dispatchSearchState({ category: CHECKBOX_GROUPS.LOCATION_FLAGS, type: DISPATCHES.UPDATE_SINGLE_VALUE, key: v[AppConstants.TYPE_KEYS.URL_KEY], value: !searchState[v[AppConstants.TYPE_KEYS.URL_KEY]] })}>
                      {v[AppConstants.TYPE_KEYS.TITLE]}
                    </Text>
                  </View>
                ))
              }
            </View>
          </View>
          <View style={styles.containerWithPadding3}>
            <View style={styles.container3}>
              <Text variant="labelLarge">Producer:</Text>
            </View>
            <Divider style={styles.divider2} />
            <View style={styles.container6}>
              {
                allProducers.length > 0 ?
                    <Picker
                      selectedValue={producer}
                      onValueChange={(itemValue, itemIndex) =>
                        setProducer(itemValue)
                      }
                    >
                      <Picker.Item label="" value="" />
                      {
                        allProducers.map((val) => 
                          <Picker.Item label={val} value={val} />
                        )
                      }
                    </Picker>
                  :
                    <TextInput
                      value={producer}
                      onChangeText={setProducer}
                      style={styles.searchInput}
                      placeholder='Producer'
                      placeholderTextColor="#777777"
                    />
              }
            </View>
          </View>
          <View style={styles.containerWithPadding4}>
            <Divider style={styles.divider2} />
            <View style={styles.container7}>
              <View style={styles.line2}>
                <CustomCheckbox
                  value={savePermanently}
                  onValueChange={() => setSavePermanently(!savePermanently)}
                  color="#3366ff"
                />
                <Text onPress={() => setSavePermanently(!savePermanently)}>
                  Save permanently
                </Text>
              </View>
            </View>
          </View>
        </View>
        <View style={{ paddingVertical: 20 }}>
        { renderButtons(true) }
        </View>
      </ScrollView>
      <Snackbar
        visible={snackVisible}
        onDismiss={() => setSnackVisible(false)}
        action={{
          label: "OK",
          onPress: () => setSnackVisible(false),
        }}
        wrapperStyle={AppConstants.STYLES.snackWrapper}
        duration={AppConstants.TEMPORARY_SNACK_DEFAULT_DURATION}
      >
        {snackText}
      </Snackbar>
    </View>
  );
}
